import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { FormCard } from "../components/ui";
import PublicPageFrame from "../components/ui/PublicPageFrame";
import { sharedApi } from "../services/sharedApi";

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation("auth");

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError(t("forgotPassword.errors.emailRequired"));
      return;
    }

    setLoading(true);
    try {
      await sharedApi.auth.forgotPassword({ email });
      setSuccess(true);
    } catch {
      setError(t("forgotPassword.errors.sendFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicPageFrame>
      <FormCard
        title={t("forgotPassword.title")}
        subtitle={success ? undefined : t("forgotPassword.description")}
        maxWidth="sm"
      >
        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t("forgotPassword.successMessage")}
            </Alert>
            <Typography variant="h6" gutterBottom>
              {t("forgotPassword.successTitle")}
            </Typography>
            <Button
              component={RouterLink}
              to="/auth"
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
            >
              {t("forgotPassword.backToLogin")}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Collapse in={!!error}>
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            </Collapse>

            <TextField
              fullWidth
              label={t("forgotPassword.emailLabel")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || !email}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{ mb: 2 }}
            >
              {loading
                ? t("forgotPassword.sending")
                : t("forgotPassword.sendLink")}
            </Button>

            <Button component={RouterLink} to="/auth" fullWidth variant="text">
              {t("forgotPassword.backToLogin")}
            </Button>
          </Box>
        )}
      </FormCard>
    </PublicPageFrame>
  );
};

export default ForgotPasswordPage;
